/**
 * Dokumentenverwaltung + Rechtsdokumente/Consent (SSO-Endpoints).
 *
 * - Mandanten-Dokumente: Betreiber laedt je Company hoch (AV, AGB, Vertraege,
 *   Rechnungen), Company-Admins sehen/laden die Dokumente ihres Mandanten.
 * - Rechtsdokumente: plattformweit versioniert; neue Version mit
 *   Zustimmungspflicht -> Consent-Gate bei allen Nutzern.
 *
 * Downloads laufen als Blob mit Bearer-Header — die Endpoints sind bewusst
 * nicht oeffentlich (Rechnungen/Vertraege sind vertraulich).
 */
import tokenService from './tokenService';

const SSO_URL = process.env.REACT_APP_SSO_URL || 'http://localhost:9000';

export const DOCUMENT_CATEGORIES = [
    { id: 'av_vertrag', name: 'AV-Vertrag' },
    { id: 'agb', name: 'AGB' },
    { id: 'vertrag', name: 'Vertrag' },
    { id: 'rechnung', name: 'Rechnung' },
    { id: 'sonstiges', name: 'Sonstiges' },
];

/** Produkt-Scope; leerer Wert = allgemein/produktübergreifend. Brand-Schreibweisen! */
export const DOCUMENT_APPLICATIONS = [
    { id: '', name: 'Allgemein' },
    { id: 'academy', name: 'online:academy' },
    { id: 'azubiweb', name: 'azubi:web' },
];

export interface CompanyDocument {
    uuid: string;
    title: string;
    category: string;
    application: string | null;
    fileName: string;
    mimeType: string;
    fileSize: number;
    createdAt: string;
    companyId: number;
}

export interface GlobalDocumentVersion {
    uuid: string;
    version: number;
    fileName: string;
    mimeType: string;
    fileSize: number;
    requiresConsent: boolean;
    publishedAt: string;
    consentCount?: number;
}

export interface GlobalDocument {
    uuid: string;
    slug: string;
    title: string;
    currentVersion: GlobalDocumentVersion | null;
    consented?: boolean | null;
}

export interface PendingConsent {
    documentTitle: string;
    slug: string;
    versionUuid: string;
    version: number;
    publishedAt: string;
    fileName: string;
}

/** JWT-Claims des eingeloggten Users (roles, companyId) fuer UI-Weichen. */
export function getTokenClaims(): { roles: string[]; companyId: number | null } {
    const token = tokenService.getToken();
    if (!token) {
        return { roles: [], companyId: null };
    }
    try {
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        return {
            roles: Array.isArray(payload.roles) ? payload.roles : [],
            companyId: typeof payload.companyId === 'number' ? payload.companyId : null,
        };
    } catch {
        return { roles: [], companyId: null };
    }
}

function authHeaders(): Record<string, string> {
    return { Authorization: `Bearer ${tokenService.getToken()}` };
}

async function jsonOrThrow(response: Response): Promise<any> {
    if (response.status === 204) {
        return null;
    }
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(data.error || `Fehler (${response.status})`);
    }
    return data;
}

async function downloadBlob(url: string, fileName: string): Promise<void> {
    const response = await fetch(url, { headers: authHeaders() });
    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Download fehlgeschlagen (${response.status})`);
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
}

// ---------------------------------------------------------------------------
// Mandanten-Dokumente
// ---------------------------------------------------------------------------

export async function listCompanyDocuments(companyId: number): Promise<CompanyDocument[]> {
    const data = await jsonOrThrow(await fetch(`${SSO_URL}/api/companies/${companyId}/documents`, { headers: authHeaders() }));
    return data.documents;
}

export async function uploadCompanyDocument(
    companyId: number,
    file: File,
    title: string,
    category: string,
    application: string = '',
): Promise<CompanyDocument> {
    const form = new FormData();
    form.append('file', file);
    form.append('title', title);
    form.append('category', category);
    form.append('application', application);

    return jsonOrThrow(await fetch(`${SSO_URL}/api/companies/${companyId}/documents`, {
        method: 'POST',
        headers: authHeaders(),
        body: form,
    }));
}

export async function downloadCompanyDocument(doc: CompanyDocument): Promise<void> {
    await downloadBlob(`${SSO_URL}/api/company-documents/${doc.uuid}/download`, doc.fileName);
}

export async function deleteCompanyDocument(uuid: string): Promise<void> {
    await jsonOrThrow(await fetch(`${SSO_URL}/api/company-documents/${uuid}`, { method: 'DELETE', headers: authHeaders() }));
}

// ---------------------------------------------------------------------------
// Rechtsdokumente (global) + Consent
// ---------------------------------------------------------------------------

export async function listGlobalDocuments(): Promise<GlobalDocument[]> {
    const data = await jsonOrThrow(await fetch(`${SSO_URL}/api/global-documents`, { headers: authHeaders() }));
    return data.documents;
}

export async function createGlobalDocument(slug: string, title: string): Promise<GlobalDocument> {
    return jsonOrThrow(await fetch(`${SSO_URL}/api/global-documents`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, title }),
    }));
}

export async function uploadGlobalDocumentVersion(
    documentUuid: string,
    file: File,
    requiresConsent: boolean,
): Promise<GlobalDocumentVersion> {
    const form = new FormData();
    form.append('file', file);
    form.append('requiresConsent', requiresConsent ? 'true' : 'false');

    return jsonOrThrow(await fetch(`${SSO_URL}/api/global-documents/${documentUuid}/versions`, {
        method: 'POST',
        headers: authHeaders(),
        body: form,
    }));
}

export async function downloadGlobalDocumentVersion(versionUuid: string, fileName: string): Promise<void> {
    await downloadBlob(`${SSO_URL}/api/global-document-versions/${versionUuid}/download`, fileName);
}

export async function getConsentStats(documentUuid: string): Promise<{
    document: { uuid: string; slug: string; title: string };
    eligibleUsers: number;
    versions: GlobalDocumentVersion[];
}> {
    return jsonOrThrow(await fetch(`${SSO_URL}/api/global-documents/${documentUuid}/consents`, { headers: authHeaders() }));
}

export async function deleteGlobalDocument(uuid: string): Promise<void> {
    await jsonOrThrow(await fetch(`${SSO_URL}/api/global-documents/${uuid}`, { method: 'DELETE', headers: authHeaders() }));
}

export async function getPendingConsents(): Promise<PendingConsent[]> {
    const data = await jsonOrThrow(await fetch(`${SSO_URL}/api/consent/pending`, { headers: authHeaders() }));
    return data.pending;
}

export async function consentToVersion(versionUuid: string): Promise<void> {
    await jsonOrThrow(await fetch(`${SSO_URL}/api/consent/${versionUuid}`, { method: 'POST', headers: authHeaders() }));
}
